import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, TextInput } from 'react-native';
import LoadingSpinner from './LoadingSpinner';
import { SPORT_CATEGORIES, AVAILABLE_TICKER_OPTIONS, getLeaguesBySport } from '../context/TickerContext';

/**
 * SGP Cache Tab
 * 
 * Combines generation and viewing:
 * - Browse all fixtures with cached SGPs
 * - Generate SGPs for any fixture
 * - View cached SGP details in drill-down format
 * - Real-time generation status
 */
const SGPCacheTab = () => {
  const [fixtures, setFixtures] = useState([]);
  const [cachedSGPs, setCachedSGPs] = useState({});
  const [selectedLeagues, setSelectedLeagues] = useState([]); // Empty = show all
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [expandedFixtures, setExpandedFixtures] = useState({});
  const [expandedSGPs, setExpandedSGPs] = useState({});
  const [generationLogs, setGenerationLogs] = useState({});
  const [bulkProgress, setBulkProgress] = useState(null); // {processed, total, successful, failed, status}
  
  // ⭐ Section collapse state for date groups
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // ⭐ NEW: Timer and stats state
  const [countdown, setCountdown] = useState(null);
  const [sgpCacheStats, setSgpCacheStats] = useState(null);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  // Dynamic leagues based on available fixtures
  const leagues = useMemo(() => {
    const uniqueLeagues = new Set();
    fixtures.forEach(fixture => {
      if (fixture.league) {
        uniqueLeagues.add(fixture.league.toLowerCase());
      }
    });
    
    const leagueOptions = [{ id: 'all', name: 'All Leagues' }];
    
    // Map league IDs to display names
    const leagueNames = {
      'nba': 'NBA',
      'nfl': 'NFL',
      'nhl': 'NHL',
      'mlb': 'MLB',
      'ncaab': 'NCAAB',
      'ncaaf': 'NCAAF',
      'mls': 'MLS',
      'epl': 'EPL',
      'uefa': 'UEFA'
    };
    
    Array.from(uniqueLeagues).sort().forEach(league => {
      leagueOptions.push({
        id: league,
        name: leagueNames[league] || league.toUpperCase()
      });
    });
    
    return leagueOptions;
  }, [fixtures]);
  
  useEffect(() => {
    fetchFixtures();
    fetchSGPCacheStats();
  }, []);
  
  // Fetch SGP status after fixtures are loaded
  useEffect(() => {
    if (fixtures.length > 0) {
      fetchAllCachedSGPs();
    }
  }, [fixtures.length]);
  
  // ⭐ Calculate countdown to next SGP auto-refresh (runs at :40 each hour)
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      let nextRefresh = new Date(now);
      
      // SGP refresh runs at :40 each hour
      if (now.getMinutes() >= 40) {
        // If past :40, next refresh is next hour's :40
        nextRefresh.setHours(now.getHours() + 1, 40, 0, 0);
      } else {
        // Before :40, next refresh is this hour's :40
        nextRefresh.setMinutes(40, 0, 0);
      }
      
      const diffMs = nextRefresh - now;
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setCountdown(`${minutes}m ${seconds}s`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // ⭐ Auto-refresh stats every 30 seconds while page is open
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      if (!loading && !generatingAll) {
        console.log('[SGPCache] Auto-refreshing stats...');
        fetchSGPCacheStats();
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(autoRefreshInterval);
  }, [loading, generatingAll]);
  
  // ⭐ Fetch REAL SGP cache stats (aggregate from actual cache, not last-run stats)
  const fetchSGPCacheStats = async () => {
    try {
      // Use new aggregate stats endpoint for REAL cache state
      const response = await fetch(`${functionsUrl}/getSGPCacheAggregateStats`);
      const data = await response.json();
      
      if (data.success && data.aggregate) {
        // Transform to expected format for UI
        setSgpCacheStats({
          success: true,
          aggregate: data.aggregate,
          stats: {
            total_fixtures: data.aggregate.total_fixtures_available,
            sgps_generated: data.aggregate.total_fixtures_cached,
            total_sgps: data.aggregate.total_sgps,
            by_league: data.by_league
          },
          uncached_sample: data.uncached_sample,
          timestamps: data.timestamps,
          updatedAt: data.timestamps?.checked_at ? { _seconds: new Date(data.timestamps.checked_at).getTime() / 1000 } : null,
          // Keep status field for UI compatibility
          status: data.aggregate.cache_coverage_percent === 100 ? 'complete' : 'partial'
        });
        console.log('[SGPCache] Aggregate stats loaded:', data.aggregate);
        return; // Success, don't fallback
      }
      throw new Error('No aggregate data in response');
    } catch (err) {
      console.error('[SGPCache] Failed to fetch aggregate stats:', err);
      // Fallback to progress endpoint if aggregate fails
      try {
        const fallbackResponse = await fetch(`${functionsUrl}/getSGPGenerationProgress`);
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.success) {
          // ⭐ Transform fallback data to include aggregate structure
          const totalFixtures = fallbackData.stats?.total_fixtures || fallbackData.total || 0;
          const cachedFixtures = fallbackData.stats?.sgps_generated || fallbackData.successful || 0;
          const totalSgps = fallbackData.stats?.total_sgps || fallbackData.totalSGPs || 0;
          
          setSgpCacheStats({
            ...fallbackData,
            aggregate: {
              total_fixtures_available: totalFixtures,
              total_fixtures_cached: cachedFixtures,
              total_fixtures_uncached: totalFixtures - cachedFixtures,
              cache_coverage_percent: totalFixtures > 0 ? Math.round((cachedFixtures / totalFixtures) * 100) : 0,
              total_sgps: totalSgps
            }
          });
          console.log('[SGPCache] Using fallback stats with calculated coverage');
        }
      } catch (fallbackErr) {
        console.error('[SGPCache] Fallback also failed:', fallbackErr);
      }
    }
  };
  
  // Fetch all fixtures
  const fetchFixtures = async () => {
    try {
      setLoading(true);
      // Increased limit to 500 to get more SGP-ready fixtures after filtering
      const response = await fetch(`${functionsUrl}/getAllCachedFixtures?limit=500&has_odds=true&include_odds=true`);
      const data = await response.json();
      
      console.log('[SGPCache] Fetched fixtures:', {
        total: data.fixtures?.length || 0,
        sample: data.fixtures?.[0]
      });
      
      if (data.success) {
        // Filter to SGP-ready fixtures (10+ total markets to ensure 5+ SGP-friendly markets)
        // Note: ~35% of markets are SGP-friendly, so need 10+ total to ensure 5+ SGP markets
        const sgpReady = data.fixtures.filter(f => {
          const marketCount = f.total_markets || 0;
          const oddsCount = f.total_odds_available || 0;
          // Need at least 10 markets AND at least 30 odds for quality SGPs
          return marketCount >= 10 && oddsCount >= 30;
        });
        
        console.log('[SGPCache] SGP-ready fixtures:', {
          total: sgpReady.length,
          filtered_out: (data.fixtures?.length || 0) - sgpReady.length
        });
        
        setFixtures(sgpReady);
      }
    } catch (err) {
      console.error('Failed to fetch fixtures:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch all cached SGPs status (lightweight check for each fixture)
  const fetchAllCachedSGPs = async () => {
    try {
      // Fetch SGP status for all fixtures in parallel
      // This is a lightweight check to determine border styling
      const fixtureIds = fixtures.map(f => f.fixture_id || f.id);
      
      if (fixtureIds.length === 0) return;
      
      console.log('[SGPCache] Fetching SGP status for', fixtureIds.length, 'fixtures...');
      
      // Fetch in parallel (limited to 10 at a time to avoid overwhelming the server)
      const batchSize = 10;
      for (let i = 0; i < fixtureIds.length; i += batchSize) {
        const batch = fixtureIds.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (fixtureId) => {
            try {
              const response = await fetch(`${functionsUrl}/getCachedSGPs?fixture_id=${fixtureId}`);
              const data = await response.json();
              
              if (data.success && data.sgps) {
                // Store lightweight status (just stats, not full SGP data)
                setCachedSGPs(prev => ({
                  ...prev,
                  [fixtureId]: {
                    stats: data.stats,
                    generated_at: data.generated_at,
                    expires_at: data.expires_at
                  }
                }));
              }
            } catch (err) {
              // Silently fail for individual fixtures
              console.debug(`[SGPCache] No SGPs for ${fixtureId}`);
            }
          })
        );
      }
      
      console.log('[SGPCache] Finished fetching SGP status');
    } catch (err) {
      console.error('Failed to fetch cached SGPs:', err);
    }
  };
  
  // Fetch cached SGPs for a specific fixture
  const fetchCachedSGPs = async (fixtureId) => {
    try {
      const response = await fetch(`${functionsUrl}/getCachedSGPs?fixture_id=${fixtureId}`);
      const data = await response.json();
      
      if (data.success && data.sgps) {
        console.log(`[SGPCache] Fetched SGPs for ${fixtureId}:`, {
          sgpsType: typeof data.sgps,
          sgpsKeys: Object.keys(data.sgps || {}),
          stats: data.stats
        });
        
        setCachedSGPs(prev => ({
          ...prev,
          [fixtureId]: {
            sgps: data.sgps,
            stats: data.stats,
            generated_at: data.generated_at,
            expires_at: data.expires_at
          }
        }));
        return data;
      }
      return null;
    } catch (err) {
      console.error(`Failed to fetch SGPs for ${fixtureId}:`, err);
      return null;
    }
  };
  
  // Generate SGPs for a fixture
  const generateSGPs = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    
    setGenerating(prev => ({ ...prev, [fixtureId]: true }));
    setGenerationLogs(prev => ({ ...prev, [fixtureId]: '🚀 Starting SGP generation...\n' }));
    
    try {
      const cachedOdds = fixture.featured_markets || fixture.markets || null;
      
      setGenerationLogs(prev => ({ 
        ...prev, 
        [fixtureId]: prev[fixtureId] + `📊 Analyzing ${fixture.total_markets} markets with ${fixture.total_odds_available} odds\n`
      }));
      
      const response = await fetch(`${functionsUrl}/generateSGPs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fixture_id: fixtureId,
          cached_odds: cachedOdds,
          options: {
            legCounts: [2, 3],
            topNPerLeg: 25,
            sportsbooks: ['DraftKings', 'Caesars', 'BetMGM', 'FanDuel', 'BetRivers', 'Unibet'],
            minEV: 0,
            smartCombosOnly: true,
            cache: true
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log(`[SGPCache] Generated SGPs for ${fixtureId}:`, {
          sgpsType: typeof result.sgps,
          sgpsKeys: Object.keys(result.sgps || {}),
          stats: result.stats
        });
        
        setGenerationLogs(prev => ({ 
          ...prev, 
          [fixtureId]: prev[fixtureId] + `✅ Generated ${result.stats.total_sgps} SGPs!\n`
        }));
        
        // Store in cache
        setCachedSGPs(prev => ({
          ...prev,
          [fixtureId]: {
            sgps: result.sgps,
            stats: result.stats,
            generated_at: new Date().toISOString()
          }
        }));
        
        // Auto-expand to show results
        setExpandedFixtures(prev => ({ ...prev, [fixtureId]: true }));
      } else {
        const errorMsg = result.error || 'Unknown error';
        let displayMsg = errorMsg;
        
        // Make error messages more user-friendly
        if (errorMsg.includes('Insufficient markets')) {
          displayMsg = '❌ Not enough SGP-friendly markets (need 5+ player/team props)\n   Try a fixture with more markets (NBA/NFL games work best)';
        }
        
        setGenerationLogs(prev => ({ 
          ...prev, 
          [fixtureId]: prev[fixtureId] + `${displayMsg}\n`
        }));
      }
    } catch (err) {
      setGenerationLogs(prev => ({ 
        ...prev, 
        [fixtureId]: prev[fixtureId] + `❌ Error: ${err.message}\n`
      }));
    } finally {
      setGenerating(prev => ({ ...prev, [fixtureId]: false }));
    }
  };
  
  // Poll for SGP generation progress
  const pollProgress = async () => {
    const maxPolls = 300; // 5 minutes max (300 * 1 second)
    let pollCount = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(`${functionsUrl}/getSGPGenerationProgress`);
        const data = await response.json();
        
        if (data.success && data.status === 'running') {
          setBulkProgress({
            processed: data.processed || 0,
            total: data.total || 0,
            successful: data.successful || 0,
            failed: data.failed || 0,
            totalSGPs: data.totalSGPs || 0,
            status: 'running',
            progress_percent: data.progress_percent || 0
          });
          
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(poll, 1000); // Poll every second
          }
        } else if (data.status === 'complete') {
          setBulkProgress({
            ...data,
            status: 'complete'
          });
          
          // ⭐ Auto-refresh ALL data including stats
          setTimeout(() => {
            fetchFixtures();
            fetchAllCachedSGPs();
            fetchSGPCacheStats(); // ⭐ Refresh stats too!
            setBulkProgress(null);
            setGeneratingAll(false);
          }, 2000);
        } else {
          // Idle or no progress
          if (pollCount > 0) {
            // Was running, now stopped
            setBulkProgress(null);
            setGeneratingAll(false);
          }
        }
      } catch (err) {
        console.error('[SGPCache] Progress poll error:', err);
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, 2000); // Retry after 2s
        }
      }
    };
    
    poll();
  };
  
  // Generate SGPs for ALL fixtures (client-side sequential to avoid timeouts)
  const generateAllSGPs = async () => {
    try {
      setGeneratingAll(true);
      
      // Filter to fixtures that don't have cached SGPs yet
      const fixturesToProcess = filteredFixtures.filter(f => {
        const fixtureId = f.fixture_id || f.id;
        const cached = cachedSGPs[fixtureId];
        const gameStatus = getGameStatus(f.start_date);
        
        // Skip if already cached, already started, or in the past
        if (cached) return false;
        if (gameStatus.status === 'past' || gameStatus.status === 'live') return false;
        
        // Only process NBA games with enough markets (for reliability)
        const league = (f.league || '').toLowerCase();
        if (!['nba', 'nfl', 'ncaab', 'ncaaf'].includes(league)) return false;
        
        return true;
      });
      
      const total = fixturesToProcess.length;
      
      if (total === 0) {
        alert('✅ All valid fixtures already have SGPs cached!');
        setGeneratingAll(false);
        return;
      }
      
      console.log(`[SGPCache] Starting sequential generation for ${total} fixtures...`);
      setBulkProgress({ 
        processed: 0, 
        total, 
        successful: 0, 
        failed: 0, 
        totalSGPs: 0,
        status: 'running',
        currentFixture: null
      });
      
      let successful = 0;
      let failed = 0;
      let totalSGPs = 0;
      
      // Process fixtures ONE AT A TIME (sequential to avoid timeouts)
      for (let i = 0; i < total; i++) {
        const fixture = fixturesToProcess[i];
        const fixtureId = fixture.fixture_id || fixture.id;
        
        // Update progress with current fixture
        setBulkProgress(prev => ({
          ...prev,
          processed: i,
          currentFixture: `${fixture.away_team} @ ${fixture.home_team}`,
          progress_percent: Math.round((i / total) * 100)
        }));
        
        console.log(`[SGPCache] Processing ${i + 1}/${total}: ${fixture.away_team} @ ${fixture.home_team}`);
        
        try {
          // Get cached odds for this fixture
          const cachedOdds = fixture.featured_markets || fixture.markets || {};
          
          const response = await fetch(`${functionsUrl}/generateSGPs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
              fixture_id: fixtureId,
              cached_odds: Object.keys(cachedOdds).length > 0 ? cachedOdds : undefined,
              options: {
                legCounts: [2], // Only 2-leg for speed
                topNPerLeg: 25,
                sportsbooks: ['DraftKings', 'BetMGM', 'Caesars'],
                minEV: 0,
                smartCombosOnly: true
              }
        })
      });
      
          const result = await response.json();
      
          if (result.success) {
            successful++;
            totalSGPs += result.stats?.total_sgps || 0;
            
            // Update cache state
            setCachedSGPs(prev => ({
              ...prev,
              [fixtureId]: {
                sgps: result.sgps,
                stats: result.stats,
                generated_at: new Date().toISOString()
              }
            }));
            
            console.log(`   ✅ Generated ${result.stats?.total_sgps || 0} SGPs`);
      } else {
            failed++;
            console.log(`   ❌ Failed: ${result.error}`);
      }
    } catch (err) {
          failed++;
          console.error(`   ❌ Error: ${err.message}`);
        }
        
        // Update progress
        setBulkProgress(prev => ({
          ...prev,
          processed: i + 1,
          successful,
          failed,
          totalSGPs,
          progress_percent: Math.round(((i + 1) / total) * 100)
        }));
        
        // Small delay between fixtures to avoid rate limiting
        if (i < total - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      // Complete!
      setBulkProgress(prev => ({
        ...prev,
        status: 'complete',
        processed: total,
        progress_percent: 100
      }));
      
      console.log(`[SGPCache] ✅ Bulk generation complete: ${successful}/${total} successful, ${totalSGPs} total SGPs`);
      
      // Auto-clear progress after 5 seconds
      setTimeout(() => {
        setBulkProgress(null);
        setGeneratingAll(false);
      }, 5000);
      
    } catch (err) {
      console.error('Failed bulk SGP generation:', err);
      alert(`❌ ERROR: ${err.message}`);
      setGeneratingAll(false);
      setBulkProgress(null);
    }
  };
  
  // ⭐ NEW: Server-side batch generation (faster than client-side)
  const triggerServerSideBatch = async (forceRetryFailed = false) => {
    try {
      setGeneratingAll(true);
      setBulkProgress({ 
        processed: 0, 
        total: 0, 
        successful: 0, 
        failed: 0, 
        totalSGPs: 0,
        status: 'starting',
        currentFixture: null
      });
      
      console.log(`[SGPCache] Triggering server-side batch refresh (retryFailed: ${forceRetryFailed})...`);
      
      const response = await fetch(`${functionsUrl}/refreshAllSGPs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 200,
          sync: false, // Async mode
          options: {
            forceRetryFailed,
            skipRecentMinutes: forceRetryFailed ? 0 : 50, // Skip nothing if retrying failed
            legCounts: [2, 3],
            maxCombinations: 100
          }
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('[SGPCache] Server batch started:', result);
        // Poll for progress
        pollProgress();
      } else {
        throw new Error(result.error || 'Failed to start batch');
      }
      
    } catch (err) {
      console.error('[SGPCache] Server batch error:', err);
      alert(`❌ ERROR: ${err.message}`);
      setGeneratingAll(false);
      setBulkProgress(null);
    }
  };
  
  // Toggle fixture expansion
  const toggleFixture = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const isExpanding = !expandedFixtures[fixtureId];
    
    setExpandedFixtures(prev => ({ ...prev, [fixtureId]: isExpanding }));
    
    // If expanding and no cached SGPs yet, try to fetch
    if (isExpanding && !cachedSGPs[fixtureId]) {
      await fetchCachedSGPs(fixtureId);
    }
  };
  
  // Toggle SGP expansion (show all legs)
  const toggleSGP = (fixtureId, legCount, sgpIndex) => {
    const key = `${fixtureId}_${legCount}_${sgpIndex}`;
    setExpandedSGPs(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  // Get game status for color coding (defined before filteredFixtures to avoid hoisting issues)
  const getGameStatus = (startDate) => {
    if (!startDate) return { status: 'upcoming', flag: '🔵', color: '#3B82F6' };
    
    try {
      const gameTime = startDate._seconds 
        ? new Date(startDate._seconds * 1000) 
        : new Date(startDate);
      const now = new Date();
      const diffHours = (gameTime - now) / (1000 * 60 * 60);
      
      if (diffHours < -3) return { status: 'past', flag: '⚪', color: '#4B5563' };
      if (diffHours < 0) return { status: 'live', flag: '🟢', color: '#10B981' };
      if (diffHours < 2) return { status: 'live_soon', flag: '🟠', color: '#F59E0B' };
      return { status: 'upcoming', flag: '🔵', color: '#3B82F6' };
    } catch {
      return { status: 'upcoming', flag: '🔵', color: '#3B82F6' };
    }
  };
  
  // ⭐ Get date group for categorizing fixtures into sections
  const getDateGroup = (startDate) => {
    if (!startDate) return { key: 'unknown', label: "Unknown Date's Fixtures", order: 99, emoji: '❓' };
    
    try {
      const gameTime = startDate._seconds 
        ? new Date(startDate._seconds * 1000) 
        : new Date(startDate);
      
      const now = new Date();
      
      // Reset times to midnight for day comparison
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const gameDay = new Date(gameTime.getFullYear(), gameTime.getMonth(), gameTime.getDate());
      
      const diffDays = Math.floor((gameDay - today) / (1000 * 60 * 60 * 24));
      
      // Past games (more than 3 hours ago)
      if (gameTime < new Date(now.getTime() - 3 * 60 * 60 * 1000)) {
        return { key: 'past', label: "Past Fixtures", order: 100, emoji: '⏪' };
      }
      
      // Today
      if (diffDays === 0) {
        return { key: 'today', label: "Today's Fixtures", order: 0, emoji: '🔥' };
      }
      
      // Tomorrow
      if (diffDays === 1) {
        return { key: 'tomorrow', label: "Tomorrow's Fixtures", order: 1, emoji: '📅' };
      }
      
      // This week (2-6 days from now) - show day names
      if (diffDays >= 2 && diffDays <= 6) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[gameDay.getDay()];
        return { 
          key: `day_${diffDays}`, 
          label: `${dayName}'s Fixtures`, 
          order: diffDays, 
          emoji: '📆' 
        };
      }
      
      // Next week (7-13 days)
      if (diffDays >= 7 && diffDays <= 13) {
        return { key: 'next_week', label: "Next Week's Fixtures", order: 10, emoji: '📅' };
      }
      
      // Next month (14-30 days)
      if (diffDays >= 14 && diffDays <= 30) {
        return { key: 'next_month', label: "Next Month's Fixtures", order: 20, emoji: '🗓️' };
      }
      
      // Future (31+ days)
      return { key: 'future', label: "Future Fixtures", order: 30, emoji: '🔮' };
      
    } catch {
      return { key: 'unknown', label: "Unknown Date's Fixtures", order: 99, emoji: '❓' };
    }
  };
  
  // Toggle section collapse
  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };
  
  // Calculate available SGP combinations based on SGP-FRIENDLY markets only
  const calculateAvailableCombos = (fixture) => {
    if (!fixture.featured_markets && !fixture.markets) {
      return { twoLeg: 0, threeLeg: 0, sgpMarkets: 0, totalOdds: 0 };
    }
    
    // Get all markets
    const markets = fixture.featured_markets || fixture.markets || {};
    
    // Filter to SGP-friendly markets (full game only, no quarters/halves)
    const sgpFriendlyMarkets = Object.entries(markets).filter(([marketKey, marketData]) => {
      if (!marketData || (!marketData.data?.length && !marketData.odds_count)) {
        return false;
      }
      
      // Get market name (use market_name if available, otherwise the key)
      const marketName = (marketData.market_name || marketKey || '').toLowerCase();
      
      // Exclude partial game markets
      if (marketName.includes('1st') || 
          marketName.includes('2nd') || 
          marketName.includes('3rd') || 
          marketName.includes('4th') || 
          marketName.includes('half') || 
          marketName.includes('quarter')) {
        return false;
      }
      
      return true;
    });
    
    const sgpMarketCount = sgpFriendlyMarkets.length;
    
    // Count total odds across SGP-friendly markets only
    let totalOdds = 0;
    sgpFriendlyMarkets.forEach(([marketKey, marketData]) => {
      totalOdds += marketData.data?.length || marketData.odds_count || 0;
    });
    
    // Calculate combinations using actual SGP-friendly odds
    // 2-leg: C(n, 2) = n * (n-1) / 2
    // 3-leg: C(n, 3) = n * (n-1) * (n-2) / 6
    // Cap at backend limits to show realistic generation counts
    const maxFor2Leg = 20; // Backend generates up to 20 choose 2 = 190 combos
    const maxFor3Leg = 15; // Backend generates up to 15 choose 3 = 455 combos
    
    // Use actual odds count (don't artificially cap the display number)
    const n2 = totalOdds; // Show actual potential
    const n3 = totalOdds;
    
    // Calculate combinations
    const twoLeg = n2 >= 2 ? Math.floor((n2 * (n2 - 1)) / 2) : 0;
    const threeLeg = n3 >= 3 ? Math.floor((n3 * (n3 - 1) * (n3 - 2)) / 6) : 0;
    
    // Flag if we'll hit backend limits
    const twoLegCapped = n2 > maxFor2Leg;
    const threeLegCapped = n3 > maxFor3Leg;
    const twoLegActual = twoLegCapped ? Math.floor((maxFor2Leg * (maxFor2Leg - 1)) / 2) : twoLeg;
    const threeLegActual = threeLegCapped ? Math.floor((maxFor3Leg * (maxFor3Leg - 1) * (maxFor3Leg - 2)) / 6) : threeLeg;
    
    return { 
      twoLeg,                    // Total possible combinations
      threeLeg,                  // Total possible combinations
      twoLegActual,              // What backend will actually generate (capped)
      threeLegActual,            // What backend will actually generate (capped)
      twoLegCapped,              // Flag if hitting backend limit
      threeLegCapped,            // Flag if hitting backend limit
      sgpMarkets: sgpMarketCount,
      totalOdds
    };
  };
  
  // Filter fixtures
  const filteredFixtures = useMemo(() => {
    return fixtures.filter(fixture => {
      // League filter - if selectedLeagues is empty, show all
      if (selectedLeagues.length > 0) {
        const fixtureLeague = (fixture.league || '').toLowerCase();
        const matchesLeague = selectedLeagues.some(selectedId => {
          const selectedLower = selectedId.toLowerCase();
          // Direct match
          if (fixtureLeague === selectedLower || fixtureLeague.includes(selectedLower)) return true;
          // Handle common aliases
          if (selectedLower === 'ncaab' && fixtureLeague.includes('ncaa') && fixtureLeague.includes('basketball')) return true;
          if (selectedLower === 'ncaaf' && fixtureLeague.includes('ncaa') && fixtureLeague.includes('football')) return true;
          if (selectedLower === 'epl' && (fixtureLeague.includes('premier') || fixtureLeague.includes('epl'))) return true;
          if (selectedLower === 'champions_league' && fixtureLeague.includes('champions')) return true;
          return false;
        });
        if (!matchesLeague) return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const homeTeam = (fixture.home_team || '').toLowerCase();
        const awayTeam = (fixture.away_team || '').toLowerCase();
        if (!homeTeam.includes(query) && !awayTeam.includes(query)) {
          return false;
        }
      }
      
      return true;
    }).sort((a, b) => {
      // Sort by: LIVE → Upcoming (soonest first) → Past (most recent first)
      const getTimestamp = (fixture) => {
        if (!fixture.start_date) return 0;
        if (fixture.start_date._seconds) return fixture.start_date._seconds * 1000;
        return new Date(fixture.start_date).getTime();
      };
      
      const aTime = getTimestamp(a);
      const bTime = getTimestamp(b);
      const aStatus = getGameStatus(a.start_date);
      const bStatus = getGameStatus(b.start_date);
      
      // Priority 1: LIVE games first
      if (aStatus.status === 'live' && bStatus.status !== 'live') return -1;
      if (bStatus.status === 'live' && aStatus.status !== 'live') return 1;
      
      // Priority 2: Starting soon games
      if (aStatus.status === 'live_soon' && bStatus.status !== 'live_soon' && bStatus.status !== 'live') return -1;
      if (bStatus.status === 'live_soon' && aStatus.status !== 'live_soon' && aStatus.status !== 'live') return 1;
      
      // Priority 3: Upcoming games (soonest first)
      if (aStatus.status === 'upcoming' && bStatus.status === 'upcoming') {
        return aTime - bTime; // Ascending (soonest first)
      }
      
      // Priority 4: Past games (most recent first)
      if (aStatus.status === 'past' && bStatus.status === 'past') {
        return bTime - aTime; // Descending (most recent first)
      }
      
      // Mixed: upcoming before past
      if (aStatus.status === 'upcoming' && bStatus.status === 'past') return -1;
      if (aStatus.status === 'past' && bStatus.status === 'upcoming') return 1;
      
      // Default: by time
      return aTime - bTime;
    });
  }, [fixtures, selectedLeagues, searchQuery]);
  
  // ⭐ Group fixtures by date category
  const groupedFixtures = useMemo(() => {
    const groups = {};
    
    filteredFixtures.forEach(fixture => {
      const dateGroup = getDateGroup(fixture.start_date);
      
      if (!groups[dateGroup.key]) {
        groups[dateGroup.key] = {
          key: dateGroup.key,
          label: dateGroup.label,
          order: dateGroup.order,
          emoji: dateGroup.emoji,
          fixtures: []
        };
      }
      
      groups[dateGroup.key].fixtures.push(fixture);
    });
    
    // Convert to array and sort by order
    return Object.values(groups).sort((a, b) => a.order - b.order);
  }, [filteredFixtures]);
  
  // Format date
  const formatDate = (dateField) => {
    if (!dateField) return 'Unknown date';
    try {
      const date = dateField._seconds 
        ? new Date(dateField._seconds * 1000) 
        : new Date(dateField);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };
  
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header with quick refresh */}
      <View style={[styles.header, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
        <View>
          <Text style={styles.headerTitle}>🎯 SGP Cache</Text>
          <Text style={styles.headerSubtitle}>
            Generate & browse cached Same Game Parlays
          </Text>
        </View>
        <TouchableOpacity 
          style={{ 
            backgroundColor: '#1F2937', 
            paddingHorizontal: 12, 
            paddingVertical: 8, 
            borderRadius: 8,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6
          }}
          onPress={() => fetchSGPCacheStats()}
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner size={14} color="#9CA3AF" />
          ) : (
            <Text style={{ color: '#9CA3AF', fontSize: 14 }}>🔄 Refresh Stats</Text>
          )}
        </TouchableOpacity>
      </View>
      
      {/* ⭐ Timer & Stats Card */}
      <View style={styles.timerCard}>
        <View style={styles.timerRow}>
          {/* Countdown Timer */}
          <View style={styles.timerSection}>
            <Text style={styles.timerLabel}>⏱️ Next Auto-Refresh</Text>
            <Text style={styles.timerValue}>{countdown || 'Calculating...'}</Text>
            <Text style={styles.timerSubtext}>Runs every hour at :40</Text>
          </View>
          
          {/* Last Updated */}
          <View style={styles.timerSection}>
            <Text style={styles.timerLabel}>📅 Last Updated</Text>
            <Text style={styles.timerValue}>
              {loading ? 'Loading...' : sgpCacheStats?.updatedAt ? (
                (() => {
                  const date = sgpCacheStats.updatedAt._seconds 
                    ? new Date(sgpCacheStats.updatedAt._seconds * 1000)
                    : new Date(sgpCacheStats.updatedAt);
                  const now = new Date();
                  const diffMs = now - date;
                  const diffMins = Math.floor(diffMs / 60000);
                  if (diffMins < 60) return `${diffMins}m ago`;
                  const diffHours = Math.floor(diffMins / 60);
                  if (diffHours < 24) return `${diffHours}h ago`;
                  return date.toLocaleDateString();
                })()
              ) : 'Never'}
            </Text>
            <Text style={styles.timerSubtext}>
              {loading ? 'Fetching stats...' : sgpCacheStats?.updatedAt ? (
                (() => {
                  const date = sgpCacheStats.updatedAt._seconds 
                    ? new Date(sgpCacheStats.updatedAt._seconds * 1000)
                    : new Date(sgpCacheStats.updatedAt);
                  return date.toLocaleTimeString();
                })()
              ) : 'Not yet generated'}
            </Text>
          </View>
        </View>
        
        {/* Cache Stats Summary - Main numbers */}
        {sgpCacheStats?.aggregate && (
          <View style={styles.cacheStatsSummary}>
            <View style={styles.cacheStatItem}>
              <Text style={styles.cacheStatValue}>{sgpCacheStats.aggregate.total_fixtures_available || 0}</Text>
              <Text style={styles.cacheStatLabel}>FIXTURES</Text>
            </View>
            <View style={styles.cacheStatItem}>
              <Text style={styles.cacheStatValue}>{sgpCacheStats.aggregate.total_fixtures_cached || 0}</Text>
              <Text style={styles.cacheStatLabel}>CACHED</Text>
            </View>
            <View style={styles.cacheStatItem}>
              <Text style={[styles.cacheStatValue, { color: '#10B981' }]}>
                {(sgpCacheStats.aggregate.total_sgps || 0).toLocaleString()}
              </Text>
              <Text style={styles.cacheStatLabel}>TOTAL SGPs</Text>
            </View>
          </View>
        )}
        
        {/* ⭐ Coverage Percentages - Today, Tomorrow, Total */}
        {sgpCacheStats?.aggregate && (
          <View style={{ 
            flexDirection: 'row', 
            justifyContent: 'space-around', 
            backgroundColor: 'rgba(59, 130, 246, 0.1)', 
            borderRadius: 8, 
            padding: 12, 
            marginTop: 10 
          }}>
            {/* Today's Coverage */}
            <View style={{ alignItems: 'center', flex: 1 }}>
              {(() => {
                const today = sgpCacheStats.aggregate.today || { total: 0, cached: 0 };
                const pct = today.total > 0 ? Math.round((today.cached / today.total) * 100) : 100;
                const color = today.total === 0 ? '#6B7280' : pct === 100 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
                return (
                  <>
                    <Text style={{ color, fontSize: 20, fontWeight: '700' }}>
                      {today.total > 0 ? `${pct}%` : '—'}
                    </Text>
                    <Text style={{ color: '#EF4444', fontSize: 10, fontWeight: '600', marginTop: 2 }}>
                      🔥 TODAY
                    </Text>
                    <Text style={{ color: '#6B7280', fontSize: 9 }}>
                      {today.cached}/{today.total}
                    </Text>
                  </>
                );
              })()}
            </View>
            
            {/* Divider */}
            <View style={{ width: 1, backgroundColor: 'rgba(156, 163, 175, 0.3)', marginHorizontal: 8 }} />
            
            {/* Tomorrow's Coverage */}
            <View style={{ alignItems: 'center', flex: 1 }}>
              {(() => {
                const tomorrow = sgpCacheStats.aggregate.tomorrow || { total: 0, cached: 0 };
                const pct = tomorrow.total > 0 ? Math.round((tomorrow.cached / tomorrow.total) * 100) : 100;
                const color = tomorrow.total === 0 ? '#6B7280' : pct === 100 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
                return (
                  <>
                    <Text style={{ color, fontSize: 20, fontWeight: '700' }}>
                      {tomorrow.total > 0 ? `${pct}%` : '—'}
                    </Text>
                    <Text style={{ color: '#F59E0B', fontSize: 10, fontWeight: '600', marginTop: 2 }}>
                      📅 TOMORROW
                    </Text>
                    <Text style={{ color: '#6B7280', fontSize: 9 }}>
                      {tomorrow.cached}/{tomorrow.total}
                    </Text>
                  </>
                );
              })()}
            </View>
            
            {/* Divider */}
            <View style={{ width: 1, backgroundColor: 'rgba(156, 163, 175, 0.3)', marginHorizontal: 8 }} />
            
            {/* Total Coverage */}
            <View style={{ alignItems: 'center', flex: 1 }}>
              {(() => {
                const pct = sgpCacheStats.aggregate.cache_coverage_percent || 0;
                const color = pct >= 50 ? '#10B981' : pct >= 25 ? '#F59E0B' : '#EF4444';
                return (
                  <>
                    <Text style={{ color, fontSize: 20, fontWeight: '700' }}>
                      {pct}%
                    </Text>
                    <Text style={{ color: '#3B82F6', fontSize: 10, fontWeight: '600', marginTop: 2 }}>
                      📊 TOTAL
                    </Text>
                    <Text style={{ color: '#6B7280', fontSize: 9 }}>
                      {sgpCacheStats.aggregate.total_fixtures_cached}/{sgpCacheStats.aggregate.total_fixtures_available}
                    </Text>
                  </>
                );
              })()}
            </View>
          </View>
        )}
        
        {/* ⭐ Later games info (2+ days) */}
        {sgpCacheStats?.aggregate?.later?.total > 0 && (
          <View style={{ 
            backgroundColor: 'rgba(107, 114, 128, 0.1)', 
            borderRadius: 6, 
            padding: 8, 
            marginTop: 8,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Text style={{ color: '#9CA3AF', fontSize: 11 }}>
              🗓️ 2+ days away: {sgpCacheStats.aggregate.later.cached}/{sgpCacheStats.aggregate.later.total} cached
            </Text>
            <Text style={{ color: '#6B7280', fontSize: 10, fontStyle: 'italic' }}>
              SGPs may not be available yet
            </Text>
          </View>
        )}
        
        {/* Uncached Warning - show if any nearby games are missing */}
        {sgpCacheStats?.aggregate && (
          (sgpCacheStats.aggregate.today?.cached < sgpCacheStats.aggregate.today?.total) ||
          (sgpCacheStats.aggregate.tomorrow?.cached < sgpCacheStats.aggregate.tomorrow?.total)
        ) && (
          <View style={{ 
            backgroundColor: 'rgba(251, 191, 36, 0.1)', 
            borderRadius: 8, 
            padding: 12, 
            marginTop: 12,
            borderLeftWidth: 3,
            borderLeftColor: '#F59E0B'
          }}>
            <Text style={{ color: '#F59E0B', fontWeight: '600', marginBottom: 4 }}>
              ⏳ {((sgpCacheStats.aggregate.today?.total || 0) - (sgpCacheStats.aggregate.today?.cached || 0)) + 
                  ((sgpCacheStats.aggregate.tomorrow?.total || 0) - (sgpCacheStats.aggregate.tomorrow?.cached || 0))} upcoming fixtures need SGP generation
            </Text>
            <Text style={{ color: '#9CA3AF', fontSize: 12 }}>
              Auto-generates hourly at :40. Or click "Generate All SGPs" to process now.
            </Text>
          </View>
        )}
        
        {/* League Breakdown - now uses by_league from response root */}
        {sgpCacheStats?.by_league && Object.keys(sgpCacheStats.by_league).length > 0 && (
          <View style={styles.leagueBreakdown}>
            <Text style={styles.leagueBreakdownTitle}>SGPs by League:</Text>
            <View style={styles.leagueBreakdownRow}>
              {Object.entries(sgpCacheStats.by_league)
                .filter(([_, data]) => data.sgps > 0)
                .sort((a, b) => b[1].sgps - a[1].sgps)
                .slice(0, 6)
                .map(([league, data]) => (
                  <View key={league} style={styles.leagueBreakdownItem}>
                    <Text style={styles.leagueBreakdownLeague}>{league.toUpperCase()}</Text>
                    <Text style={styles.leagueBreakdownCount}>{data.sgps}</Text>
                  </View>
                ))}
            </View>
          </View>
        )}
      </View>
      
      {/* Search */}
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search by team..."
          placeholderTextColor="#9CA3AF"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>
      
      {/* League Filter - Sport Categories & Individual Leagues */}
      <View style={styles.leagueFilterContainer}>
        {/* Sport Category Quick Toggles */}
        <View style={styles.sportCategoriesRow}>
          <Text style={styles.filterSectionLabel}>Filter by Sport:</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.sportCategoriesContent}
          >
            {SPORT_CATEGORIES.filter(cat => ['all_basketball', 'all_football', 'all_hockey', 'all_baseball', 'all_soccer'].includes(cat.id)).map((category) => {
              const sportLeagues = getLeaguesBySport(category.sport);
              const selectedCount = sportLeagues.filter(l => selectedLeagues.includes(l)).length;
              const allSelected = selectedCount === sportLeagues.length && selectedCount > 0;
              const someSelected = selectedCount > 0 && !allSelected;
              
              return (
                <TouchableOpacity
                  key={category.id}
                  onPress={() => {
                    if (allSelected) {
                      // Deselect all leagues in this sport
                      setSelectedLeagues(prev => prev.filter(l => !sportLeagues.includes(l)));
                    } else {
                      // Select all leagues in this sport
                      setSelectedLeagues(prev => [...new Set([...prev, ...sportLeagues])]);
                    }
                  }}
                  style={[
                    styles.sportCategoryButton,
                    allSelected && styles.sportCategoryButtonActive,
                    someSelected && styles.sportCategoryButtonPartial
                  ]}
                >
                  <Text style={[
                    styles.sportCategoryText,
                    allSelected && styles.sportCategoryTextActive,
                    someSelected && styles.sportCategoryTextPartial
                  ]}>
                    {category.label.split(' ')[0]} {category.label.split(' ').slice(1).join(' ')}
                    {someSelected ? ` (${selectedCount})` : ''}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
        
        {/* Individual League Toggles */}
        <View style={styles.leagueTogglesRow}>
          <Text style={styles.filterSectionLabel}>Leagues:</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.leagueFilterContent}
          >
            {/* Show All button */}
            <TouchableOpacity
              style={[
                styles.leagueButton,
                selectedLeagues.length === 0 && styles.leagueButtonActive
              ]}
              onPress={() => setSelectedLeagues([])}
            >
              <Text style={[
                styles.leagueButtonText,
                selectedLeagues.length === 0 && styles.leagueButtonTextActive
              ]}>
                All
              </Text>
            </TouchableOpacity>
            
            {/* Only show leagues that have fixtures in our data */}
            {leagues.filter(l => l.id !== 'all').map(league => {
              const isSelected = selectedLeagues.includes(league.id);
              return (
                <TouchableOpacity
                  key={league.id}
                  style={[
                    styles.leagueButton,
                    isSelected && styles.leagueButtonActive
                  ]}
                  onPress={() => {
                    if (isSelected) {
                      setSelectedLeagues(prev => prev.filter(l => l !== league.id));
                    } else {
                      setSelectedLeagues(prev => [...prev, league.id]);
                    }
                  }}
                >
                  <Text style={[
                    styles.leagueButtonText,
                    isSelected && styles.leagueButtonTextActive
                  ]}>
                    {league.name}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
        
        {/* Quick Actions */}
        {selectedLeagues.length > 0 && (
          <View style={styles.filterQuickActions}>
            <Text style={styles.filterActiveText}>
              {selectedLeagues.length} league{selectedLeagues.length !== 1 ? 's' : ''} selected
            </Text>
            <TouchableOpacity onPress={() => setSelectedLeagues([])}>
              <Text style={styles.filterClearText}>Clear All</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      
      {/* Action Buttons */}
      <View style={styles.actionButtonsContainer}>
        <TouchableOpacity 
          style={[styles.actionButton, styles.fetchButton, { backgroundColor: '#3B82F6' }]} 
          onPress={() => { fetchFixtures(); fetchAllCachedSGPs(); fetchSGPCacheStats(); }}
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner size={16} color="#FFFFFF" />
          ) : (
            <Text style={styles.actionButtonText}>🔄 Refresh Data</Text>
          )}
        </TouchableOpacity>
        
        {/* Retry Failed Button - only show if there are actual failed fixtures (not just uncached) */}
        {/* A fixture is "failed" if it has an sgp_cache entry but with 0 SGPs or error flag */}
        {sgpCacheStats?.aggregate?.total_fixtures_failed > 0 && (
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: '#F59E0B' }]} 
            onPress={() => triggerServerSideBatch(true)}
            disabled={generatingAll}
          >
            {generatingAll ? (
              <LoadingSpinner size={16} color="#FFFFFF" />
            ) : (
              <Text style={styles.actionButtonText}>🔄 Retry {sgpCacheStats.aggregate.total_fixtures_failed} Failed</Text>
            )}
          </TouchableOpacity>
        )}
        
        <TouchableOpacity 
          style={[styles.actionButton, styles.generateAllButton]} 
          onPress={() => triggerServerSideBatch(false)}
          disabled={generatingAll}
        >
          {generatingAll ? (
            <LoadingSpinner size={16} color="#FFFFFF" />
          ) : (
            <Text style={styles.actionButtonText}>🤖 Generate All SGPs</Text>
          )}
        </TouchableOpacity>
      </View>
      
      {/* Bulk Generation Progress */}
      {bulkProgress && (
        <View style={styles.progressContainer}>
          <View style={styles.progressHeader}>
            <Text style={styles.progressTitle}>
              {bulkProgress.status === 'starting' ? '🚀 Starting...' : 
               bulkProgress.status === 'complete' ? '✅ Complete!' : 
               '🤖 Generating SGPs...'}
            </Text>
            <Text style={styles.progressStats}>
              {bulkProgress.processed}/{bulkProgress.total} fixtures • {bulkProgress.successful || 0} successful • {bulkProgress.failed || 0} failed • {bulkProgress.totalSGPs || 0} SGPs
            </Text>
          </View>
          
          {/* Current Fixture */}
          {bulkProgress.currentFixture && bulkProgress.status === 'running' && (
            <Text style={styles.currentFixture}>
              📍 Processing: {bulkProgress.currentFixture}
            </Text>
          )}
          
          {/* Progress Bar */}
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBarFill, { width: `${bulkProgress.progress_percent || 0}%` }]} />
          </View>
          
          <Text style={styles.progressPercent}>{bulkProgress.progress_percent || 0}%</Text>
          
          {/* Estimated Time */}
          {bulkProgress.status === 'running' && bulkProgress.processed > 0 && (
            <Text style={styles.estimatedTime}>
              ⏱️ ~{Math.ceil((bulkProgress.total - bulkProgress.processed) * 2)} min remaining (avg 2 min/fixture)
            </Text>
          )}
        </View>
      )}
      
      {/* Loading */}
      {loading && (
        <View style={styles.loadingContainer}>
          <LoadingSpinner size={32} color="#3B82F6" />
          <Text style={styles.loadingText}>Loading fixtures...</Text>
        </View>
      )}
      
      {/* Fixtures List - Grouped by Date */}
      {!loading && (
        <View style={styles.fixturesContainer}>
          <Text style={styles.resultsCount}>
            {filteredFixtures.length} fixture{filteredFixtures.length !== 1 ? 's' : ''} available in {groupedFixtures.length} group{groupedFixtures.length !== 1 ? 's' : ''}
          </Text>
          
          {groupedFixtures.map(group => {
            const isCollapsed = collapsedSections[group.key];
            const cachedCount = group.fixtures.filter(f => cachedSGPs[f.fixture_id || f.id]).length;
            
            // Get unique leagues in this group with counts
            const leagueCounts = {};
            group.fixtures.forEach(f => {
              const league = (f.league || 'unknown').toUpperCase();
              leagueCounts[league] = (leagueCounts[league] || 0) + 1;
            });
            const groupLeagues = Object.entries(leagueCounts).sort((a, b) => b[1] - a[1]); // Sort by count desc
            
            return (
              <View key={group.key} style={styles.dateGroupContainer}>
                {/* Section Header */}
                <TouchableOpacity 
                  style={styles.sectionHeader}
                  onPress={() => toggleSection(group.key)}
                >
                  <View style={styles.sectionHeaderLeft}>
                    <Text style={styles.sectionEmoji}>{group.emoji}</Text>
                    <Text style={styles.sectionTitle}>{group.label}</Text>
                    <View style={styles.sectionBadge}>
                      <Text style={styles.sectionBadgeText}>
                        {group.fixtures.length} game{group.fixtures.length !== 1 ? 's' : ''}
                      </Text>
                    </View>
                    {cachedCount > 0 && (
                      <View style={[styles.sectionBadge, styles.sectionBadgeCached]}>
                        <Text style={[styles.sectionBadgeText, { color: '#10B981' }]}>
                          {cachedCount} cached
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.sectionHeaderRight}>
                    {/* League badges */}
                    <View style={styles.sectionLeagues}>
                      {groupLeagues.slice(0, 5).map(([league, count]) => (
                        <View key={league} style={styles.sectionLeagueBadge}>
                          <Text style={styles.sectionLeagueText}>{league}</Text>
                          <Text style={styles.sectionLeagueCount}>{count}</Text>
                        </View>
                      ))}
                      {groupLeagues.length > 5 && (
                        <Text style={styles.sectionLeagueMore}>+{groupLeagues.length - 5}</Text>
                      )}
                    </View>
                    <Text style={styles.sectionExpandIcon}>{isCollapsed ? '▶' : '▼'}</Text>
                  </View>
                </TouchableOpacity>
                
                {/* Section Content */}
                {!isCollapsed && group.fixtures.map(fixture => {
                  const fixtureId = fixture.fixture_id || fixture.id;
                  const isExpanded = expandedFixtures[fixtureId];
                  const isGenerating = generating[fixtureId];
                  const cached = cachedSGPs[fixtureId];
                  const gameStatus = getGameStatus(fixture.start_date);
                  const combos = calculateAvailableCombos(fixture);
                  
                  return (
                    <View 
                      key={fixtureId} 
                      style={[
                        styles.fixtureCard,
                        { 
                          borderLeftWidth: 4, 
                          borderLeftColor: gameStatus.color,
                          borderStyle: cached ? 'solid' : 'dotted',
                          borderWidth: cached ? 1 : 2
                        }
                      ]}
                    >
                      {/* Fixture Header */}
                      <TouchableOpacity 
                        style={styles.fixtureHeader}
                        onPress={() => toggleFixture(fixture)}
                      >
                        <View style={styles.fixtureHeaderLeft}>
                          <View style={styles.fixtureHeaderTop}>
                            <Text style={styles.fixtureLeague}>{(fixture.league || '').toUpperCase()}</Text>
                            <View style={styles.statusBadge}>
                              <Text style={styles.statusFlag}>{gameStatus.flag}</Text>
                            </View>
                            {cached && (
                              <View style={styles.cachedBadge}>
                                <Text style={styles.cachedBadgeText}>
                                  ✓ {cached.stats?.total_sgps || 0} SGPs
                                </Text>
                              </View>
                            )}
                          </View>
                          <Text style={styles.fixtureTeams}>
                            {fixture.away_team} @ {fixture.home_team}
                          </Text>
                          <Text style={styles.fixtureDate}>{formatDate(fixture.start_date)}</Text>
                          <View style={styles.fixtureStatsRow}>
                            <Text style={styles.fixtureStats}>
                              {fixture.total_markets} markets • {fixture.total_odds_available} odds
                            </Text>
                            {fixture.total_markets && (
                              <View style={[
                                styles.sgpReadyBadge,
                                Math.floor(fixture.total_markets * 0.35) >= 5 ? styles.sgpReadyBadgeGood : styles.sgpReadyBadgeWarn
                              ]}>
                                <Text style={[
                                  styles.sgpReadyBadgeText,
                                  { color: Math.floor(fixture.total_markets * 0.35) >= 5 ? '#10B981' : '#F59E0B' }
                                ]}>
                                  ~{Math.floor(fixture.total_markets * 0.35)} SGP markets
                                </Text>
                              </View>
                            )}
                          </View>
                          
                          {/* Available Combo Counts */}
                          {combos.sgpMarkets > 0 && (
                            <View style={styles.comboCountsRow}>
                              <Text style={styles.comboCountsLabel}>Available Combinations:</Text>
                              <View style={styles.comboCountsValues}>
                                <View style={styles.comboCountBadge}>
                                  <Text style={styles.comboCountLabel}>2-leg:</Text>
                                  <Text style={styles.comboCountValue}>
                                    {combos.twoLeg >= 1000000 
                                      ? `${(combos.twoLeg / 1000000).toFixed(2)}M`
                                      : combos.twoLeg >= 1000 
                                        ? `${(combos.twoLeg / 1000).toFixed(1)}K` 
                                        : combos.twoLeg}
                                  </Text>
                                </View>
                                <View style={styles.comboCountBadge}>
                                  <Text style={styles.comboCountLabel}>3-leg:</Text>
                                  <Text style={styles.comboCountValue}>
                                    {combos.threeLeg >= 1000000 
                                      ? `${(combos.threeLeg / 1000000).toFixed(2)}M`
                                      : combos.threeLeg >= 1000 
                                        ? `${(combos.threeLeg / 1000).toFixed(1)}K` 
                                        : combos.threeLeg}
                                  </Text>
                                </View>
                                <Text style={styles.comboCountsHint}>
                                  ({combos.sgpMarkets} markets, {combos.totalOdds} odds)
                                </Text>
                              </View>
                            </View>
                          )}
                        </View>
                        
                        <View style={styles.fixtureHeaderRight}>
                          <TouchableOpacity
                            style={styles.generateButton}
                            onPress={(e) => {
                              e.stopPropagation();
                              generateSGPs(fixture);
                            }}
                            disabled={isGenerating}
                          >
                            {isGenerating ? (
                              <LoadingSpinner size={14} color="#FFFFFF" />
                            ) : (
                              <Text style={styles.generateButtonText}>
                                {cached ? '🔄 Regenerate' : '✨ Generate'}
                              </Text>
                            )}
                          </TouchableOpacity>
                          <Text style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</Text>
                        </View>
                      </TouchableOpacity>
                
                      {/* Generation Log */}
                      {generationLogs[fixtureId] && (
                        <View style={styles.logContainer}>
                          <Text style={styles.logText}>{generationLogs[fixtureId]}</Text>
                        </View>
                      )}
                      
                      {/* Expanded: Show SGPs */}
                      {isExpanded && cached && (
                        <View style={styles.sgpsContainer}>
                          {Object.entries(cached.sgps || {}).map(([legCount, sgps]) => {
                            // Ensure sgps is an array
                            const sgpArray = Array.isArray(sgps) ? sgps : [];
                            
                            if (sgpArray.length === 0) return null;
                            
                            return (
                            <View key={legCount} style={styles.legCountSection}>
                              <Text style={styles.legCountHeader}>
                                🎯 {legCount} ({sgpArray.length} SGPs)
                              </Text>
                              
                              {sgpArray.slice(0, 10).map((sgp, index) => {
                                const sgpKey = `${fixtureId}_${legCount}_${index}`;
                                const sgpExpanded = expandedSGPs[sgpKey];
                                
                                return (
                                  <View key={index} style={styles.sgpCard}>
                                    <TouchableOpacity
                                      style={styles.sgpHeader}
                                      onPress={() => toggleSGP(fixtureId, legCount, index)}
                                    >
                                      <View style={styles.sgpHeaderLeft}>
                                        <Text style={styles.sgpRank}>#{index + 1}</Text>
                                        <View>
                                          <Text style={styles.sgpOdds}>
                                            {sgp.combined_price > 0 ? '+' : ''}{sgp.combined_price}
                                          </Text>
                                          <Text style={styles.sgpBook}>@ {sgp.best_book}</Text>
                                        </View>
                                      </View>
                                      <View style={styles.sgpHeaderRight}>
                                        <View style={[styles.evBadge, sgp.ev_percent > 0 ? styles.evBadgePositive : null]}>
                                          <Text style={styles.evBadgeText}>
                                            {sgp.ev_source === 'calculated' && '⚠️ '}
                                            {sgp.ev_source === 'api' && '✓ '}
                                            {sgp.ev_percent > 0 ? '+' : ''}{sgp.ev_percent.toFixed(1)}% EV
                                          </Text>
                                        </View>
                                        <Text style={styles.expandIcon}>{sgpExpanded ? '▼' : '▶'}</Text>
                                      </View>
                                    </TouchableOpacity>
                                    
                                    {/* Expanded: Show Legs */}
                                    {sgpExpanded && (
                                      <View style={styles.legsContainer}>
                                        {/* AI Fair Price & Correlation */}
                                        {(sgp.ai_fair_price || sgp.correlation_factor) && (
                                          <View style={styles.aiDataContainer}>
                                            {sgp.ai_fair_price && (
                                              <View style={styles.aiDataRow}>
                                                <Text style={styles.aiDataLabel}>🤖 AI Fair Price:</Text>
                                                <Text style={styles.aiDataValue}>
                                                  {sgp.ai_fair_price > 0 ? '+' : ''}{sgp.ai_fair_price}
                                                </Text>
                                              </View>
                                            )}
                                            {sgp.correlation_factor !== null && sgp.correlation_factor !== undefined && (
                                              <View style={styles.aiDataRow}>
                                                <Text style={styles.aiDataLabel}>
                                                  🔗 Correlation:
                                                  {sgp.has_correlation ? ' ✓' : ' ⚠️'}
                                                </Text>
                                                <Text style={styles.aiDataValue}>
                                                  {(sgp.correlation_factor * 100).toFixed(0)}%
                                                </Text>
                                              </View>
                                            )}
                                          </View>
                                        )}
                                        
                                        <View style={styles.baselineRow}>
                                          <Text style={styles.baselineLabel}>Baseline:</Text>
                                          <Text style={styles.baselineValue}>
                                            {sgp.fair_price > 0 ? '+' : ''}{sgp.fair_price || 'N/A'} @ {sgp.baseline_book || 'DraftKings'}
                                          </Text>
                                        </View>
                                        
                                        {(sgp.legs || []).map((leg, legIndex) => (
                                          <View key={legIndex} style={styles.legRow}>
                                            <Text style={styles.legNumber}>Leg {legIndex + 1}</Text>
                                            <View style={styles.legDetails}>
                                              <Text style={styles.legMarket}>{leg.market_name || leg.market}</Text>
                                              <Text style={styles.legSelection}>
                                                {leg.selection} {leg.selection_line || ''}
                                              </Text>
                                              {leg.price && (
                                                <Text style={styles.legPrice}>
                                                  {leg.price > 0 ? '+' : ''}{leg.price}
                                                </Text>
                                              )}
                                            </View>
                                          </View>
                                        ))}
                                        
                                        {Array.isArray(sgp.all_books) && sgp.all_books.length > 1 && (
                                          <View style={styles.allBooksContainer}>
                                            <Text style={styles.allBooksHeader}>All Books:</Text>
                                            {sgp.all_books.map((book, bookIndex) => (
                                              <View key={bookIndex} style={styles.bookRow}>
                                                <Text style={styles.bookName}>{book.sportsbook}</Text>
                                                <View style={styles.bookPriceContainer}>
                                                  <Text style={styles.bookPrice}>
                                                    {book.price > 0 ? '+' : ''}{book.price}
                                                  </Text>
                                                  {book.ev_percent !== undefined && book.ev_percent !== null && (
                                                    <Text style={[
                                                      styles.bookEV,
                                                      book.ev_percent > 0 ? styles.bookEVPositive : styles.bookEVNegative
                                                    ]}>
                                                      ({book.ev_source === 'api' ? '✓' : '⚠️'} {book.ev_percent > 0 ? '+' : ''}{book.ev_percent.toFixed(1)}% EV)
                                                    </Text>
                                                  )}
                                                </View>
                                              </View>
                                            ))}
                                          </View>
                                        )}
                                      </View>
                                    )}
                                  </View>
                                );
                              })}
                              
                              {sgpArray.length > 10 && (
                                <Text style={styles.moreText}>
                                  + {sgpArray.length - 10} more {legCount} SGPs
                                </Text>
                              )}
                            </View>
                            );
                          })}
                          
                          {/* Generation Stats */}
                          {cached.stats && (
                            <View style={styles.statsContainer}>
                              <Text style={styles.statsHeader}>📊 Generation Stats:</Text>
                              <View style={styles.statsGrid}>
                                {cached.stats.combinations_tested && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Combinations Tested:</Text>
                                    <Text style={styles.statValue}>{cached.stats.combinations_tested.toLocaleString()}</Text>
                                  </View>
                                )}
                                {cached.stats.markets_used && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Markets Used:</Text>
                                    <Text style={styles.statValue}>{cached.stats.markets_used}</Text>
                                  </View>
                                )}
                                {cached.stats.odds_used && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Odds Analyzed:</Text>
                                    <Text style={styles.statValue}>{cached.stats.odds_used.toLocaleString()}</Text>
                                  </View>
                                )}
                                {cached.stats.total_sgps && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Total SGPs Generated:</Text>
                                    <Text style={styles.statValue}>{cached.stats.total_sgps}</Text>
                                  </View>
                                )}
                              </View>
                            </View>
                          )}
                          
                          {cached.generated_at && (
                            <Text style={styles.generatedAt}>
                              Generated: {new Date(cached.generated_at).toLocaleString()}
                            </Text>
                          )}
                        </View>
                      )}
                
                      {/* No Cache Message */}
                      {isExpanded && !cached && !isGenerating && (
                        <View style={styles.noCacheContainer}>
                          <Text style={styles.noCacheText}>
                            No cached SGPs yet. Click "Generate" to create them.
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#111827',
  },
  contentContainer: {
    padding: 16,
  },
  header: {
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#E5E7EB',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#9CA3AF',
  },
  searchContainer: {
    marginBottom: 12,
  },
  searchInput: {
    height: 40,
    backgroundColor: '#1F2937',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    color: '#E5E7EB',
    borderWidth: 1,
    borderColor: '#374151',
  },
  leagueFilterContainer: {
    marginBottom: 16,
    backgroundColor: '#1F2937',
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: '#374151',
  },
  sportCategoriesRow: {
    marginBottom: 10,
  },
  sportCategoriesContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterSectionLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sportCategoryButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#111827',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#374151',
  },
  sportCategoryButtonActive: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    borderColor: '#8B5CF6',
  },
  sportCategoryButtonPartial: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    borderColor: '#F59E0B',
  },
  sportCategoryText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#9CA3AF',
  },
  sportCategoryTextActive: {
    color: '#A78BFA',
    fontWeight: '600',
  },
  sportCategoryTextPartial: {
    color: '#FBBF24',
    fontWeight: '600',
  },
  leagueTogglesRow: {
    marginBottom: 8,
  },
  leagueFilterContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  leagueButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    backgroundColor: '#111827',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#374151',
  },
  leagueButtonActive: {
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    borderColor: '#10B981',
  },
  leagueButtonText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#9CA3AF',
  },
  leagueButtonTextActive: {
    color: '#10B981',
    fontWeight: '600',
  },
  filterQuickActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  filterActiveText: {
    fontSize: 11,
    color: '#6B7280',
  },
  filterClearText: {
    fontSize: 11,
    color: '#3B82F6',
    fontWeight: '600',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    marginBottom: 16,
    gap: 8,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 42,
  },
  fetchButton: {
    backgroundColor: '#3B82F6',
  },
  generateAllButton: {
    backgroundColor: '#10B981',
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
  loadingContainer: {
    padding: 30,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: '#9CA3AF',
  },
  fixturesContainer: {
    flex: 1,
  },
  resultsCount: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 12,
    fontWeight: '600',
  },
  // ⭐ Date Group Section Styles
  dateGroupContainer: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1F2937',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#374151',
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    flexWrap: 'wrap',
    gap: 8,
  },
  sectionEmoji: {
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#E5E7EB',
  },
  sectionBadge: {
    backgroundColor: '#374151',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 12,
  },
  sectionBadgeCached: {
    backgroundColor: '#065F46',
  },
  sectionBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#9CA3AF',
  },
  sectionHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sectionLeagues: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
    maxWidth: 300,
  },
  sectionLeagueBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#374151',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
    gap: 4,
  },
  sectionLeagueText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#9CA3AF',
  },
  sectionLeagueCount: {
    fontSize: 9,
    fontWeight: '700',
    color: '#6B7280',
    backgroundColor: '#1F2937',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
  },
  sectionLeagueMore: {
    fontSize: 10,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  sectionExpandIcon: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: 'bold',
  },
  fixtureCard: {
    backgroundColor: '#1F2937',
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#374151',
    overflow: 'hidden',
  },
  fixtureHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
  },
  fixtureHeaderLeft: {
    flex: 1,
  },
  fixtureHeaderTop: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  fixtureLeague: {
    fontSize: 10,
    fontWeight: '700',
    color: '#3B82F6',
    marginRight: 8,
    textTransform: 'uppercase',
  },
  statusBadge: {
    marginRight: 8,
  },
  statusFlag: {
    fontSize: 10,
  },
  cachedBadge: {
    backgroundColor: '#065F46',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  cachedBadgeText: {
    fontSize: 9,
    color: '#10B981',
    fontWeight: '600',
  },
  fixtureTeams: {
    fontSize: 14,
    fontWeight: '600',
    color: '#E5E7EB',
    marginBottom: 4,
  },
  fixtureDate: {
    fontSize: 11,
    color: '#9CA3AF',
    marginBottom: 2,
  },
  fixtureStatsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  fixtureStats: {
    fontSize: 10,
    color: '#6B7280',
  },
  sgpReadyBadge: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  sgpReadyBadgeGood: {
    backgroundColor: '#065F46',
  },
  sgpReadyBadgeWarn: {
    backgroundColor: '#92400E',
  },
  sgpReadyBadgeText: {
    fontSize: 9,
    fontWeight: '600',
  },
  fixtureHeaderRight: {
    alignItems: 'flex-end',
  },
  generateButton: {
    backgroundColor: '#3B82F6',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginBottom: 8,
    minWidth: 90,
    alignItems: 'center',
  },
  generateButtonText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '600',
  },
  expandIcon: {
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: 'bold',
  },
  logContainer: {
    backgroundColor: '#111827',
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  logText: {
    fontSize: 11,
    color: '#9CA3AF',
    fontFamily: 'Courier',
  },
  sgpsContainer: {
    borderTopWidth: 1,
    borderTopColor: '#374151',
    padding: 12,
  },
  legCountSection: {
    marginBottom: 16,
  },
  legCountHeader: {
    fontSize: 14,
    fontWeight: '700',
    color: '#E5E7EB',
    marginBottom: 8,
  },
  sgpCard: {
    backgroundColor: '#111827',
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#374151',
    overflow: 'hidden',
  },
  sgpHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
  },
  sgpHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sgpRank: {
    fontSize: 16,
    fontWeight: '700',
    color: '#3B82F6',
    marginRight: 12,
  },
  sgpOdds: {
    fontSize: 16,
    fontWeight: '700',
    color: '#10B981',
  },
  sgpBook: {
    fontSize: 10,
    color: '#9CA3AF',
  },
  sgpHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  evBadge: {
    backgroundColor: '#1F2937',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  evBadgePositive: {
    backgroundColor: '#065F46',
  },
  evBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#10B981',
  },
  legsContainer: {
    borderTopWidth: 1,
    borderTopColor: '#374151',
    padding: 10,
  },
  baselineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#374151',
  },
  baselineLabel: {
    fontSize: 11,
    color: '#9CA3AF',
    marginRight: 8,
  },
  baselineValue: {
    fontSize: 12,
    color: '#E5E7EB',
    fontWeight: '600',
  },
  legRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  legNumber: {
    fontSize: 11,
    fontWeight: '700',
    color: '#3B82F6',
    width: 40,
  },
  legDetails: {
    flex: 1,
  },
  legMarket: {
    fontSize: 11,
    color: '#9CA3AF',
    marginBottom: 2,
  },
  legSelection: {
    fontSize: 12,
    fontWeight: '600',
    color: '#E5E7EB',
  },
  legPrice: {
    fontSize: 10,
    color: '#10B981',
    marginTop: 2,
  },
  allBooksContainer: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  allBooksHeader: {
    fontSize: 10,
    color: '#9CA3AF',
    marginBottom: 4,
    fontWeight: '600',
  },
  bookRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  bookName: {
    fontSize: 10,
    color: '#9CA3AF',
    flex: 1,
  },
  bookPriceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  bookPrice: {
    fontSize: 10,
    color: '#E5E7EB',
    fontWeight: '600',
  },
  bookEV: {
    fontSize: 9,
    fontWeight: '500',
  },
  bookEVPositive: {
    color: '#10B981',
  },
  bookEVNegative: {
    color: '#EF4444',
  },
  moreText: {
    fontSize: 11,
    color: '#6B7280',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  generatedAt: {
    fontSize: 10,
    color: '#6B7280',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 12,
  },
  noCacheContainer: {
    padding: 20,
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  noCacheText: {
    fontSize: 13,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  aiDataContainer: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderRadius: 6,
    padding: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  aiDataRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 2,
  },
  aiDataLabel: {
    fontSize: 10,
    color: '#93C5FD',
    fontWeight: '600',
  },
  aiDataValue: {
    fontSize: 10,
    color: '#E5E7EB',
    fontWeight: '700',
  },
  statsContainer: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.3)',
  },
  statsHeader: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '700',
    marginBottom: 8,
  },
  statsGrid: {
    gap: 6,
  },
  statItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  statLabel: {
    fontSize: 10,
    color: '#9CA3AF',
  },
  statValue: {
    fontSize: 11,
    color: '#E5E7EB',
    fontWeight: '700',
  },
  // Combo counts styles
  comboCountsRow: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  comboCountsLabel: {
    fontSize: 11,
    color: '#9CA3AF',
    marginBottom: 6,
    fontWeight: '600',
  },
  comboCountsValues: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  comboCountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1F2937',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    marginRight: 8,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: '#374151',
  },
  comboCountLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    marginRight: 4,
  },
  comboCountValue: {
    fontSize: 12,
    fontWeight: '700',
    color: '#10B981',
  },
  comboCountsHint: {
    fontSize: 10,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  // ⭐ Timer & Stats Card Styles
  timerCard: {
    backgroundColor: '#1F2937',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#374151',
  },
  timerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  timerSection: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  timerLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 4,
    fontWeight: '600',
  },
  timerValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#8B5CF6',
    fontVariant: ['tabular-nums'],
  },
  timerSubtext: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  cacheStatsSummary: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  cacheStatItem: {
    alignItems: 'center',
  },
  cacheStatValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#E5E7EB',
  },
  cacheStatLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  leagueBreakdown: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#374151',
  },
  leagueBreakdownTitle: {
    fontSize: 11,
    color: '#9CA3AF',
    marginBottom: 8,
    fontWeight: '600',
  },
  leagueBreakdownRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  leagueBreakdownItem: {
    backgroundColor: '#111827',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  leagueBreakdownLeague: {
    fontSize: 10,
    fontWeight: '700',
    color: '#3B82F6',
  },
  leagueBreakdownCount: {
    fontSize: 12,
    fontWeight: '700',
    color: '#10B981',
  },
  // Bulk progress styles
  progressContainer: {
    backgroundColor: '#1F2937',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  progressHeader: {
    marginBottom: 12,
  },
  progressTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#E5E7EB',
    marginBottom: 4,
  },
  progressStats: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  progressBarContainer: {
    height: 24,
    backgroundColor: '#374151',
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 12,
    transition: 'width 0.3s ease',
  },
  progressPercent: {
    fontSize: 16,
    fontWeight: '700',
    color: '#10B981',
    textAlign: 'center',
  },
  currentFixture: {
    fontSize: 13,
    color: '#a259ff',
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '500',
  },
  estimatedTime: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: 6,
  },
});

export default SGPCacheTab;
